import React, { useState, useEffect, useCallback, useMemo, MouseEvent } from "react";
import { Rnd, RndDragCallback, RndResizeCallback } from "react-rnd";
import { Box, Paper, Avatar, useTheme, useMediaQuery, Typography, CircularProgress } from "@mui/material";
import chatbotProfileIcon from "../../../../assets/img/chatbot-profile-icon.svg";
import MessageMenu from "./components/MessageMenu";
import ChatInputArea from "./components/ChatInputArea";
import ChatHeader from "./components/ChatHeader";
import ChatHistorySidebar from "./components/ChatHistorySidebar";
import { ChatWindowProps, Message } from "../../../../shared/types/chatWindow.types";
import ChatMessageItem from "./components/ChatMessageItem";
import CircularLoader from "../../../../shared/components/loader/circularProgress";

const DEFAULT_WIDTH = 550;
const DEFAULT_HEIGHT = 550;
const MARGIN = 18;

const messageBubbleBaseStyles = {
    px: 2,
    py: 1,
    borderRadius: "18px",
    maxWidth: "80%",
    width: "fit-content",
    fontSize: "0.9rem",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    whiteSpace: "normal",
    lineHeight: 1.4,
    "& p": { margin: 0.3 },
    "& table": {
        display: "block",
        overflowX: "auto",
        maxWidth: "100%",
        borderCollapse: "collapse",
        margin: "4px 0",
        whiteSpace: "nowrap",
    },
    "& th, & td": {
        border: "1px solid #000000ff",
        padding: "6px 10px",
        fontSize: "0.85rem",
        textAlign: "left",
    },
};

const markdownComponents = {
    table: ({ node, ...props }: any) => <table {...props} />,
    th: ({ node, ...props }: any) => <th {...props} />,
    td: ({ node, ...props }: any) => <td {...props} />,
};

const getInitialWindowState = () => {
    if (typeof window === "undefined") {
        return { x: 0, y: 0, width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT };
    }
    const cw = document.documentElement.clientWidth;
    const ch = document.documentElement.clientHeight;

    const width = Math.min(DEFAULT_WIDTH, cw - MARGIN * 2);
    const height = Math.min(DEFAULT_HEIGHT, ch - MARGIN * 2);
    const x = Math.max(MARGIN, cw - width - MARGIN);
    const y = Math.max(MARGIN, ch - height - MARGIN);

    return { x, y, width, height };
};

const ChatWindow: React.FC<ChatWindowProps> = ({
    messages,
    status,
    currentStage,
    thinkingStages,
    showMentions,
    filteredProperties,
    isLoadingProps,
    input,
    previousChats,
    onSelectPreviousChat,
    onNewChat,
    onLoadMoreChats,
    isFetchingMore,
    deleteConversation,
    setIsOpen,
    handleInputChange,
    handleSelectProperty,
    handleSend,
    handleStop,
    isLoadingHistory,
    inputRef,
    messagesEndRef,
    scrollContainerRef,
    onScrollMessages,
    isFetchingOlderMessages,
    onWheelMessages,
    isRecording,
    isTranscribing,
    startRecording,
    stopRecording,
    activeAudioId,
    playbackState,
    onTogglePlayAudio,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
    const isTouchDevice = useMediaQuery("(hover: none)");

    const alwaysShowActions = useMemo(
        () => isSmallScreen || isTouchDevice,
        [isSmallScreen, isTouchDevice]
    );

    const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [activeMessage, setActiveMessage] = useState<Message | null>(null);

    const initial = useMemo(() => getInitialWindowState(), []);
    const [isMaximized, setIsMaximized] = useState(false);
    const [hasDragged, setHasDragged] = useState(false);
    const [position, setPosition] = useState({ x: initial.x, y: initial.y });
    const [size, setSize] = useState({ width: initial.width, height: initial.height });
    const [prevSizePos, setPrevSizePos] = useState({
        x: initial.x,
        y: initial.y,
        width: initial.width,
        height: initial.height,
    });

    useEffect(() => {
        const handleResize = () => {
            if (isMobile) return;

            const cw = document.documentElement.clientWidth;
            const ch = document.documentElement.clientHeight;

            if (isMaximized) {
                setSize({ width: cw, height: ch });
                setPosition({ x: 0, y: 0 });
            } else {
                setSize((prev) => {
                    const newW = Math.min(prev.width, cw);
                    const newH = Math.min(prev.height, ch);

                    setPosition((pos) =>
                        !hasDragged
                            ? { x: Math.max(0, cw - newW - MARGIN), y: Math.max(0, ch - newH - MARGIN) }
                            : { x: Math.min(Math.max(0, pos.x), cw - newW), y: Math.min(Math.max(0, pos.y), ch - newH) }
                    );

                    return { width: newW, height: newH };
                });
            }
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [isMaximized, isMobile, hasDragged]);

    const toggleMaximize = useCallback(() => {
        setIsMaximized((prevMaximized) => {
            if (prevMaximized) {
                setPosition({ x: prevSizePos.x, y: prevSizePos.y });
                setSize({ width: prevSizePos.width, height: prevSizePos.height });
            } else {
                setPrevSizePos({ ...position, ...size });
                setPosition({ x: 0, y: 0 });
                setSize({
                    width: document.documentElement.clientWidth,
                    height: document.documentElement.clientHeight,
                });
            }
            return !prevMaximized;
        });
    }, [position, size, prevSizePos]);

    const handleMenuOpen = useCallback((event: MouseEvent<HTMLElement>, msg: Message) => {
        setMenuAnchorEl(event.currentTarget);
        setActiveMessage(msg);
    }, []);

    const handleMenuClose = useCallback(() => {
        setMenuAnchorEl(null);
        setActiveMessage(null);
    }, []);

    const handleToggleHistory = useCallback(() => {
        setIsHistoryOpen((prev) => !prev);
    }, []);

    const handleCloseHistory = useCallback(() => {
        setIsHistoryOpen(false);
    }, []);

    const handleNewChatAction = useCallback(() => {
        onNewChat?.();
        setIsHistoryOpen(false);
    }, [onNewChat]);

    const handleCloseWindow = useCallback(() => {
        setIsOpen(false);
    }, [setIsOpen]);

    const handleMouseEnterMessage = useCallback((id: string) => {
        setHoveredMessageId(id);
    }, []);

    const handleMouseLeaveMessage = useCallback(() => {
        setHoveredMessageId(null);
    }, []);

    const handleDragStop: RndDragCallback = useCallback((_, d) => {
        const cw = document.documentElement.clientWidth;
        const ch = document.documentElement.clientHeight;
        setPosition({
            x: Math.max(0, Math.min(d.x, cw - size.width)),
            y: Math.max(0, Math.min(d.y, ch - size.height)),
        });
        setHasDragged(true);
    }, [size.width, size.height]);

    const handleResizeStop: RndResizeCallback = useCallback((_e, _direction, ref, _delta, pos) => {
        const cw = document.documentElement.clientWidth;
        const ch = document.documentElement.clientHeight;
        const newWidth = ref.offsetWidth;
        const newHeight = ref.offsetHeight;

        setSize({ width: newWidth, height: newHeight });
        setPosition({
            x: Math.max(0, Math.min(pos.x, cw - newWidth)),
            y: Math.max(0, Math.min(pos.y, ch - newHeight)),
        });
        setHasDragged(true);
    }, []);

    const chatContent = (
        <Paper
            elevation={0}
            sx={{
                position: isMobile ? "fixed" : "relative",
                ...(isMobile
                    ? {
                        bottom: 0,
                        right: 0,
                        width: "100vw",
                        height: "100dvh",
                        borderRadius: 0,
                        zIndex: 1500,
                    }
                    : {
                        width: "100%",
                        height: "100%",
                        borderRadius: isMaximized ? 0 : "20px",
                    }),
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                border: isMobile || isMaximized ? "none" : "1px solid #5FB7CF",
                backgroundColor: "#e4edef",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                transition: isMaximized ? "border-radius 0.2s" : "none",
            }}
        >
            <ChatHeader
                isMobile={isMobile}
                isMaximized={isMaximized}
                isHistoryOpen={isHistoryOpen}
                onNewChat={handleNewChatAction}
                onToggleHistory={handleToggleHistory}
                onToggleMaximize={toggleMaximize}
                onClose={handleCloseWindow}
            />

            <ChatHistorySidebar
                isOpen={isHistoryOpen}
                previousChats={previousChats}
                isFetchingMore={isFetchingMore}
                onClose={handleCloseHistory}
                onSelectChat={onSelectPreviousChat}
                onLoadMore={onLoadMoreChats}
                onDeleteChat={deleteConversation}
            />

            <Box
                ref={scrollContainerRef}
                onScroll={onScrollMessages}
                onWheel={onWheelMessages}
                sx={{
                    flex: "1 1 0%",
                    minHeight: 0,
                    p: 2,
                    overflowY: "auto",
                    overflowX: "hidden",
                    bgcolor: "#e4edef",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    WebkitOverflowScrolling: "touch",
                    ...(isLoadingHistory && {
                        alignItems: "center",
                        justifyContent: "center",
                    }),
                }}
            >
                {/* Loader when pulling older messages on scroll */}
                {isFetchingOlderMessages && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 1, flexShrink: 0 }}>
                        <CircularProgress size={18} sx={{ color: "#5FB7CF" }} />
                    </Box>
                )}

                {isLoadingHistory ? (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                        <CircularLoader />
                        <Typography variant="body2" color="#64748b" fontWeight={500}>
                            Loading conversation history...
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {messages.map((msg) => (
                            <ChatMessageItem
                                key={String(msg.id)}
                                msg={msg}
                                isHovered={hoveredMessageId === String(msg.id)}
                                isActive={activeMessage?.id === String(msg.id)}
                                alwaysShowActions={alwaysShowActions}
                                baseStyles={messageBubbleBaseStyles}
                                markdownComponents={markdownComponents}
                                onMouseEnter={handleMouseEnterMessage}
                                onMouseLeave={handleMouseLeaveMessage}
                                onMenuOpen={handleMenuOpen}
                                activeAudioId={activeAudioId}
                                playbackState={playbackState}
                                onTogglePlayAudio={onTogglePlayAudio}
                            />
                        ))}

                        {status === "thinking" && currentStage !== null && (
                            <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-start", gap: 1, flexShrink: 0 }}>
                                <Avatar src={chatbotProfileIcon} alt="Chatbot" sx={{ width: 36, height: 36 }} />
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1.2,
                                        borderRadius: "18px",
                                        maxWidth: "70%",
                                        bgcolor: "#c9d7dd",
                                        color: "#0f172a",
                                        fontSize: "0.9rem",
                                        fontStyle: "italic",
                                    }}
                                >
                                    {thinkingStages[currentStage]}
                                </Box>
                            </Box>
                        )}
                        <div ref={messagesEndRef} style={{ flexShrink: 0 }} />
                    </>
                )}
            </Box>

            <ChatInputArea
                input={input}
                status={status}
                showMentions={showMentions}
                isLoadingProps={isLoadingProps}
                filteredProperties={filteredProperties}
                isMobile={isMobile}
                isMaximized={isMaximized}
                inputRef={inputRef}
                onInputChange={handleInputChange}
                onSelectProperty={handleSelectProperty}
                onSend={handleSend}
                onStop={handleStop}
                isRecording={isRecording}
                isTranscribing={isTranscribing}
                onStartRecording={startRecording}
                onStopRecording={stopRecording}
            />

            <MessageMenu
                anchorEl={menuAnchorEl}
                activeMessage={activeMessage}
                onClose={handleMenuClose}
            />
        </Paper>
    );

    if (isMobile) return chatContent;

    return (
        <Box
            sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                pointerEvents: "none",
                zIndex: 1500,
            }}
        >
            <Rnd
                size={size}
                position={position}
                onDragStop={handleDragStop}
                onResizeStop={handleResizeStop}
                minWidth={320}
                minHeight={450}
                bounds="parent"
                disableDragging={isMaximized}
                enableResizing={!isMaximized}
                dragHandleClassName="chat-header-drag"
                style={{
                    pointerEvents: "auto",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {chatContent}
            </Rnd>
        </Box>
    );
};

export default ChatWindow;